import React, { useState } from 'react';
import { FileTypes } from '../../../enums/fileTypes';
import { Button, Col, Form, Input, List, Row } from 'antd';
import axios from 'axios';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { fetchLayerDetailsFromXML } from '../../../shared/utils/fetchLayerNamesFromXML';
import GeoserverService from '../../../services/GeoserverService/geoserver.service';
import { useMutation } from '@tanstack/react-query';
import './ServiceURL.scss';

export interface ServiceURLProps {
  handleFileTypeChange: (v: FileTypes) => void;
  folderId: string;
  callback: () => void;
}

const ServiceURL: React.FC<ServiceURLProps> = ({ folderId, callback }) => {
  const [layerDetails, setLayerDetails] = useState<{ name: string; title: string; description: string }[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<{ name: string; title: string; description: string }>();
  const [isFetchingWMSURL, setIsFetchingWMSURL] = useState(false);
  const mapId = location.pathname.split('/')[3];

  const { createWMSLayer } = GeoserverService();
  const { mutateAsync: createWMSLayerAsync, isPending: isSavingLayer } = useMutation(createWMSLayer());

  const schema = yup.object().shape({
    url: yup
      .string()
      .matches(/^https:\/\/[^\s?#]+(?:\?[^\s#]*\bservice=wms\b[^\s#]*\brequest=[^&?#=\s]+[^\s#]*\bversion=[^&?#=\s]+[^\s#]*)$/i)
      .required('The Service URL is required'),
  });

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      url: '',
    },
  });

  const onSubmit = () => {
    setIsFetchingWMSURL(true);
    axios
      .get(form.getValues().url)
      .then((response) => {
        const xmlString = response.data;
        const details = fetchLayerDetailsFromXML(xmlString);
        setLayerDetails(details);
      })
      .finally(() => {
        setIsFetchingWMSURL(false);
      });
  };

  const onSaveLayer = async () => {
    await createWMSLayerAsync({
      layerName: selectedLayer?.name as string,
      capabilitiesURL: form.getValues().url,
      mapId,
      folderId,
    });
    callback();
  };

  return (
    <FormProvider {...form}>
      <Form onFinish={form.handleSubmit(onSubmit)}>
        <Row justify={'end'} gutter={[12, 12]}>
          <Col span={24} className="">
            <p className="service-url__label">Service: </p>
            <Controller name={'url'} render={({ field }) => <Input {...field} placeholder="e.g http://myserver.com/wms" />} />
          </Col>
          <Col span={6}>
            <Button type="primary" htmlType="submit" disabled={!form.formState.isValid} loading={isFetchingWMSURL}>
              Connect
            </Button>
          </Col>
        </Row>
        {Boolean(layerDetails?.length) && (
          <Row justify={'end'} gutter={[12, 12]}>
            <Col span={24}>
              <List
                header="Choose a layer from the WMS:"
                size="small"
                dataSource={layerDetails}
                renderItem={(layer) => (
                  <List.Item
                    className={['service-url__layer-item', selectedLayer?.name === layer?.name ? 'service-url__layer-item--selected' : '']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedLayer(layer)}
                  >
                    <b>{layer?.title}</b> <br />
                    {layer?.description}
                  </List.Item>
                )}
              />
            </Col>
            <Col span={6}>
              <Button type="text" disabled={!selectedLayer?.name} onClick={onSaveLayer} loading={isSavingLayer}>
                Save
              </Button>
            </Col>
          </Row>
        )}
      </Form>
    </FormProvider>
  );
};

export default ServiceURL;
